<template>
  <b-modal 
    id="modal-participant-completion" 
    centered 
    hide-footer 
    title="Peserta"
    size="xl"
    modal-class="participant-modal-wide"
    body-class="p-0"
    @hidden="onModalHidden">
    <!-- Alert Info -->
    <div class="alert mb-0 rounded-0 border-0 d-flex align-items-center justify-content-between" style="background-color: #fafafa;">
      <div>
        <i class="fe fe-info text-warning me-2" style="font-size: 1.2rem;"></i>
        <span class="text-dark">Anda dapat mengunduh semua sertifikat dan laporan batch</span>
      </div>
      <!-- Action Buttons (Top Right) -->
      <div class="d-flex justify-content-end gap-2 mb-3">
        <button class="btn btn-danger btn-sm" @click="downloadReport">
          <i class="fe fe-download me-1"></i>
          Report
        </button>
        <button class="btn btn-danger btn-sm" @click="downloadAllCertificates">
          <i class="fe fe-download me-1"></i>
          Semua Sertifikat
        </button>
      </div>
    </div>

    <div class="p-4">
      <!-- Search and Filter Bar -->
      <div class="row mb-3 justify-content-between">
        <div class="col-md-4 col-lg-4">
          <input 
            v-model="searchQuery"
            type="text" 
            class="form-control" 
            placeholder="Search by name, NIK, or email..."
            @input="handleSearch">
        </div>
        <div class="col-md-4 col-lg-3">
          <select
            v-model="companyFilter"
            @change="handleFilterChange"
            class="form-select">
            <option value="">All Company</option>
            <option v-for="company in companyOptions" :key="company" :value="company">
              {{ company }}
            </option>
          </select>
        </div>
        <div class="col-md-4 col-lg-3">
          <select 
            v-model="statusFilter"
            @change="handleFilterChange"
            class="form-select">
            <option value="">All Status</option>
            <option value="passed">Passed</option>
            <option value="not_passed">Not Passed</option>
            <option value="not_present">Not Present</option>
            <option value="in_review">In Review</option>
            <option value="waiting_pjo_approval">Waiting for PJO Approval</option>
            <option value="waiting_hse_approval">Waiting for HSE Approval</option>
            <option value="waiting_manager_approval">Waiting for Manager Approval</option>
            <option value="waiting_hr_approval">Waiting for HR Approval</option>
            <option value="waiting_cancellation_approval">Waiting for Cancellation Approval</option>
            <option value="in_progress">In Progress</option>
          </select>
        </div>
      </div>
      
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-danger" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mt-2">Memuat peserta...</p>
      </div>
      
      <!-- Participant Table -->
      <div v-else class="table-responsive">
        <table class="table table-hover participant-table">
          <thead style="background-color: #f8f9fa;">
            <tr>
              <th class="text-muted small font-weight-normal col-no text-center">NO</th>
              <th class="text-muted small font-weight-normal col-nik">NIK</th>
              <th class="text-muted small font-weight-normal col-employee">EMPLOYEE</th>
              <th class="text-muted small font-weight-normal col-email">EMAIL</th>
              <th class="text-muted small font-weight-normal col-company">COMPANY</th>
              <th class="text-muted small font-weight-normal col-position">JABATAN</th>
              <th class="text-muted small font-weight-normal col-score text-center">PRE TEST</th>
              <th class="text-muted small font-weight-normal col-score text-center">POST TEST</th>
              <th class="text-muted small font-weight-normal col-status">STATUS</th>
              <th class="text-muted small font-weight-normal col-certificate text-center">CERTIFICATE</th>
            </tr>
          </thead>
          <tbody>
            <!-- Empty State -->
            <tr v-if="!participantList || participantList.length === 0">
              <td colspan="10" class="text-center py-5">
                <i class="fe fe-users text-muted" style="font-size: 3rem;"></i>
                <p class="text-muted mt-2">Peserta tidak ditemukan</p>
              </td>
            </tr>
            
            <tr v-for="(participant, index) in participantList" :key="index">
              <td class="align-middle text-center">{{ getParticipantRowNumber(index) }}</td>
              <td class="align-middle text-nowrap">{{ participant.nik || '-' }}</td>
              <td class="align-middle">{{ participant.fullname || participant.name || participant.employee || '-' }}</td>
              <td class="align-middle text-break">{{ participant.email || '-' }}</td>
              <td class="align-middle">{{ participant.company || '-' }}</td>
              <td class="align-middle">{{ participant.position_level || participant.position || '-' }}</td>
              <td class="align-middle text-center">{{ formatScore(participant.pre_test_score) }}</td>
              <td class="align-middle text-center">{{ formatScore(participant.post_test_score ?? participant.score) }}</td>
              <td class="align-middle status-cell">
                <span 
                  class="badge badge-pill px-3 py-2 status-badge" 
                  :style="getStatusStyle(participant.status)">
                  {{ formatStatus(participant.status) }}
                </span>
              </td>
              <td class="align-middle text-center">
                <button 
                  v-if="participant.certificate_url && isPassedStatus(participant.status)"
                  class="btn btn-sm btn-danger d-flex align-items-center justify-content-center" 
                  style="width: 36px; height: 36px; padding: 0;"
                  @click="downloadCertificate(participant)"
                  title="Download certificate">
                  <i class="fe fe-download" style="font-size: 16px;"></i>
                </button>
                <button 
                  v-else
                  class="btn btn-sm btn-secondary d-flex align-items-center justify-content-center" 
                  style="width: 36px; height: 36px; padding: 0;"
                  disabled>
                  <i class="fe fe-download" style="font-size: 16px;"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div class="d-flex align-items-center gap-2">
          <small class="text-muted">Showing {{ showingFrom }} to {{ showingTo }} of {{ totalParticipants }}</small>
          <div class="d-flex align-items-center ms-3">
            <label class="mb-0 me-2 text-muted" style="font-size: 0.875rem;">Per page:</label>
            <select 
              v-model.number="limit" 
              @change="handlePerPageChange"
              class="form-select form-select-sm" 
              style="width: auto;">
              <option v-for="option in perPageOptions" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </div>
        </div>
        <nav aria-label="Participant pagination">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: page === 1 }">
              <button class="page-link" @click="previousPage" :disabled="page === 1">
                <i class="fe fe-chevron-left"></i>
              </button>
            </li>
            <li 
              v-for="pageNum in visiblePages" 
              :key="pageNum"
              class="page-item" 
              :class="{ active: pageNum === page }">
              <button class="page-link" @click="goToPage(pageNum)">
                {{ pageNum }}
              </button>
            </li>
            <li class="page-item" :class="{ disabled: page === totalPages }">
              <button class="page-link" @click="nextPage" :disabled="page === totalPages">
                <i class="fe fe-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </b-modal>
</template>

<script>
import courseCompletionMitraAPI from '@/api/course-completion-mitra/course-completion-mitra';
import { normalizeErrorMessage } from '@/utils/error-message';
import { buildCourseCompletionReportFilename } from '@/utils/courseCompletionReportFilename';

export default {
  name: 'ModalParticipantCompletion',
  props: {
    batchId: {
      type: [Number, String],
      required: true
    },
    courseTitle: {
      type: String,
      default: ''
    },
    batchSchedules: {
      type: Array,
      default: () => []
    },
    training_type: {
      type: String,
    },
    platform: {
      type: String,
    },
  },
  data() {
    return {
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      page: 1,
      limit: 10,
      perPageOptions: [10, 25, 50, 100],
      searchQuery: '',
      statusFilter: '',
      companyFilter: '',
      companyOptions: [],
      participantList: [],
      totalParticipants: 0,
      totalPages: 1,
      isLoading: false,
      searchTimeout: null
    }
  },
  computed: {
    reportFilename() {
      return buildCourseCompletionReportFilename({
        title: this.courseTitle,
        schedules: this.batchSchedules
      });
    },
    showingFrom() {
      if (this.totalParticipants === 0) return 0;
      return (this.page - 1) * this.limit + 1;
    },
    showingTo() {
      const end = this.page * this.limit;
      return Math.min(end, this.totalParticipants);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.page - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  watch: {
    batchId: {
      immediate: true,
      handler(newBatchId) {
        if (newBatchId) {
          this.fetchParticipants();
        }
      }
    }
  },
  methods: {
    async fetchParticipants() {
      if (!this.batchId) return;
      
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          limit: this.limit
        };
        
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }

        if (this.companyFilter) {
          params.company = this.companyFilter;
        }
        
        const response = await courseCompletionMitraAPI.getBatchParticipants(this.batchId, params);
        
        if (response.data && response.data.data) {
          const { participants, pagination, companies } = response.data.data;
          
          this.participantList = participants || [];
          this.companyOptions = this.normalizeCompanyOptions(companies);
          this.totalParticipants = pagination?.total || 0;
          this.totalPages = pagination?.totalPages || 1;
          this.page = pagination?.page || 1;
        } else {
          this.participantList = [];
          this.companyOptions = [];
          this.totalParticipants = 0;
          this.totalPages = 1;
        }
      } catch (error) {
        console.error('Error fetching participants:', error);
        const errorMessage = normalizeErrorMessage(error.response?.data?.error || 'Peserta belum berhasil dimuat');
        this.$swal('Error', errorMessage, 'error');
        this.participantList = [];
        this.companyOptions = [];
        this.totalParticipants = 0;
        this.totalPages = 1;
      } finally {
        this.isLoading = false;
      }
    },
    
    handleSearch() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.page = 1; // Reset to first page on search
        this.fetchParticipants();
      }, 500);
    },
    
    handleFilterChange() {
      this.page = 1; // Reset to first page on filter change
      this.fetchParticipants();
    },
    
    formatStatus(status) {
      if (!status) return '-';
      const statusMap = {
        'in progress': 'In Progress',
        'completed': 'Passed',
        'passed': 'Passed',
        'failed': 'Not Passed',
        'not passed': 'Not Passed',
        'not_passed': 'Not Passed',
        'incomplete': 'Not Passed',
        'not completed': 'Not Passed',
        'not present': 'Not Present',
        'not_present': 'Not Present',
        'in_review': 'In Review',
        'waiting for pjo approval': 'Waiting for PJO Approval',
        'waiting_pjo_approval': 'Waiting for PJO Approval',
        'waiting for admin hse approval': 'Waiting for HSE Approval',
        'waiting_hse_approval': 'Waiting for HSE Approval',
        'waiting for manager approval': 'Waiting for Manager Approval',
        'waiting_manager_approval': 'Waiting for Manager Approval',
        'waiting for hr approval': 'Waiting for HR Approval',
        'waiting_hr_approval': 'Waiting for HR Approval',
        'waiting for cancellation approval': 'Waiting for Cancellation Approval',
        'waiting_cancellation_approval': 'Waiting for Cancellation Approval',
        'waiting for hse training unenrollment approval': 'Waiting for HSE Training Unenrollment Approval',
        'approval_manager_unenrolled': 'Waiting for Manager Approval',
        'approval_hr_unenrolled': 'Waiting for HR Approval',
        'approval_hse_unenrolled': 'Waiting for HSE Approval',
      };
      return statusMap[status?.toLowerCase()] || status;
    },

    formatScore(score) {
      return score === null || score === undefined || score === '' ? '-' : score;
    },

    isPassedStatus(status) {
      const normalizedStatus = status?.toLowerCase();
      return normalizedStatus === 'passed' || normalizedStatus === 'completed';
    },

    getStatusStyle(status) {
      const normalizedStatus = status?.toLowerCase();
      if (this.isPassedStatus(status)) {
        return {
          backgroundColor: '#d4edda',
          color: '#00d97e',
          fontWeight: '500'
        };
      } else if (normalizedStatus === 'failed' || normalizedStatus === 'not passed' || normalizedStatus === 'not_passed' || normalizedStatus === 'rejected' || normalizedStatus === 'incomplete' || normalizedStatus === 'not completed') {
        return {
          backgroundColor: '#fce4e4',
          color: '#e63757',
          fontWeight: '500'
        };
      } else if (normalizedStatus === 'not present' || normalizedStatus === 'not_present') {
        return {
          backgroundColor: '#f1f3f5',
          color: '#6c757d',
          fontWeight: '500'
        };
      } else if (normalizedStatus === 'in progress' || normalizedStatus === 'in_progress' || normalizedStatus === 'enrolled') {
        return {
          backgroundColor: '#e3f2fd',
          color: '#2c7be5',
          fontWeight: '500'
        };
      } else if (normalizedStatus === 'in_review') {
        return {
          backgroundColor: '#f2e7fe',
          color: '#6f42c1',
          fontWeight: '500'
        };
      } else if (
        normalizedStatus?.startsWith('waiting for ') ||
        normalizedStatus?.startsWith('waiting_') ||
        normalizedStatus?.startsWith('approval_')
      ) {
        return {
          backgroundColor: '#fff3cd',
          color: '#f6c343',
          fontWeight: '500'
        };
      }
      return {
        backgroundColor: '#f5f5f5',
        color: '#6c757d',
        fontWeight: '500'
      };
    },
    
    previousPage() {
      if (this.page > 1) {
        this.page--;
        this.fetchParticipants();
      }
    },
    
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        this.fetchParticipants();
      }
    },
    
    goToPage(pageNum) {
      if (pageNum !== '...' && pageNum !== this.page) {
        this.page = pageNum;
        this.fetchParticipants();
      }
    },
    
    handlePerPageChange() {
      // Reset to first page when changing items per page
      this.page = 1;
      this.fetchParticipants();
    },

    getParticipantRowNumber(index) {
      return (this.page - 1) * this.limit + index + 1;
    },
    
    async downloadReport() {
      try {
        this.isLoading = true;
        
        // Prepare the same query params used for fetching participants
        const params = {
          page: this.page,
          limit: this.limit
        };
        
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }

        if (this.companyFilter) {
          params.company = this.companyFilter;
        }
        
        const response = await courseCompletionMitraAPI.downloadBatchReport(this.batchId, params);
        
        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.reportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$swal('Success', 'Laporan berhasil diunduh.', 'success');
      } catch (error) {
        console.error('Error downloading report:', error);
        let errorMessage = 'Laporan belum berhasil diunduh';
        
        // Handle blob error response
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            console.error('Failed to parse error response:', e);
          }
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }
        
        this.$swal('Error', normalizeErrorMessage(errorMessage), 'error');
      } finally {
        this.isLoading = false;
      }
    },
    
    async downloadAllCertificates() {
      try {
        this.isLoading = true;
        
        const params = {};

        if (this.companyFilter) {
          params.company = this.companyFilter;
        }

        const response = await courseCompletionMitraAPI.downloadAllCertificates(this.batchId, params);
        
        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `batch-${this.batchId}-certificates.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$swal('Success', 'Sertifikat berhasil diunduh.', 'success');
      } catch (error) {
        console.error('Error downloading certificates:', error);
        let errorMessage = 'Sertifikat belum berhasil diunduh';
        
        // Handle blob error response
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            console.error('Failed to parse error response:', e);
          }
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }
        
        this.$swal('Error', normalizeErrorMessage(errorMessage), 'error');
      } finally {
        this.isLoading = false;
      }
    },

    normalizeCompanyOptions(companies = []) {
      const options = Array.isArray(companies)
        ? companies.filter(Boolean)
        : [];

      if (this.companyFilter && !options.includes(this.companyFilter)) {
        options.unshift(this.companyFilter);
      }

      return options;
    },
    
    downloadCertificate(participant) {
      if (!participant.certificate_url) return;

      const certPath = participant.source === 'structured_learning'
        ? `attachments/ugems-university/idp/structured-learning/certificate/${participant.certificate_url}`
        : `attachments/${participant.certificate_url}`;

      window.open(`${this.imageBaseUrl}/${certPath}`, '_blank');
    },
    
    uploadCertificate(participant) {
      console.log('Upload certificate for:', participant.fullname || participant.name || participant.employee);
      this.$emit('upload-certificate', participant);
    },
    
    viewCompletion(participant) {
      console.log('View completion for:', participant.fullname || participant.name || participant.employee);
      this.$emit('view-completion', participant);
    },

    onModalHidden() {
      // Emit hidden event to parent to reset state
      this.$emit('hidden');
      // Reset local state
      this.page = 1;
      this.searchQuery = '';
      this.statusFilter = '';
      this.companyFilter = '';
      this.companyOptions = [];
      this.participantList = [];
      this.totalParticipants = 0;
      this.totalPages = 1;
    }
  }
}
</script>

<style scoped>
/* Table Styles */
.participant-table {
  margin-bottom: 0;
  min-width: 1360px;
  table-layout: fixed;
}

.participant-table .col-no {
  width: 56px;
}

.participant-table .col-nik {
  width: 105px;
}

.participant-table .col-employee {
  width: 170px;
}

.participant-table .col-email {
  width: 220px;
}

.participant-table .col-company {
  width: 210px;
}

.participant-table .col-position {
  width: 230px;
}

.participant-table .col-score {
  width: 78px;
}

.participant-table .col-status {
  width: 190px;
}

.participant-table .col-certificate {
  width: 88px;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  font-weight: 400;
}

.table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #f1f1f1;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Badge Styles */
.badge-pill {
  border-radius: 50px;
  white-space: normal;
  line-height: 1.25;
  text-align: center;
}

/* Pagination Styles */
.pagination {
  gap: 0.25rem;
}

.pagination .page-link {
  color: #2c7be5;
  border: 1px solid #dee2e6;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  min-width: 36px;
  text-align: center;
}

.pagination .page-item.active .page-link {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.pagination .page-item.disabled .page-link {
  cursor: not-allowed;
  opacity: 0.5;
  background-color: transparent;
}

.pagination .page-link:hover:not(.disabled) {
  background-color: #f8f9fa;
  cursor: pointer;
}

/* Alert Styles */
.alert {
  border-radius: 0.375rem;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.ms-3 {
  margin-left: 1rem;
}

.form-select-sm {
  padding: 0.25rem 1.5rem 0.25rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.2rem;
}

::v-deep .participant-modal-wide .modal-dialog {
  max-width: calc(100vw - 32px);
  width: calc(100vw - 32px);
}

::v-deep .participant-modal-wide .modal-body {
  overflow-x: auto;
}

/* Search and Filter Styles */
.form-control:focus {
  border-color: #e63757;
  box-shadow: 0 0 0 0.2rem rgba(230, 55, 87, 0.25);
}

.form-select {
  cursor: pointer;
  border: 1px solid #dee2e6;
}

.form-select:focus {
  border-color: #e63757;
  box-shadow: 0 0 0 0.2rem rgba(230, 55, 87, 0.25);
}

/* Loading Spinner */
.spinner-border {
  width: 2rem;
  height: 2rem;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
